import { BASE_URL } from '../../../utils/constants.js';
import { isDangerousUrl } from '../../../utils/safeUrl.js';

/**
 * Bot protection of the contact block, visitor side.
 *
 * With `data.protect` on, Core sends phone numbers and emails masked ("+380 67 ••• •• 67") and
 * channel items without `data`/`url`. The real value is fetched only when someone acts on it —
 * a tap, a copy, a saved vCard — from the anonymous reveal endpoint. The Dashboard editor holds
 * the raw values (it loads them from the owner-only /private route), so there nothing here
 * goes to the network: an unmasked value is answered on the spot.
 */

export const MASK_CHAR = '•';

/** Core's reveal endpoint takes at most this many keys per request. */
const REVEAL_BATCH = 12;

/** True for a value Core masked. Masks always carry at least one bullet. */
export const isMaskedValue = (value) => typeof value === 'string' && value.includes(MASK_CHAR);

/** Whether the block content came from Core with its contacts hidden from bots. */
export const isProtectedContent = (blockContent) =>
  blockContent?.data?.protect === true || (blockContent?.contacts ?? []).some((item) => item?.isProtected === true);

/** Reveal key of a person's line. A person saved without an id is addressed by position. */
export const getPersonKey = (person, index, field) => `people/${person?.id ? person.id : `#${index}`}/${field}`;

/** Reveal key of a department's email, addressed the same way as a person. */
export const getDepartmentKey = (department, index) =>
  `departments/${department?.id ? department.id : `#${index}`}/email`;

/** Reveal key of a channel item. */
export const getItemKey = (item) => `item:${item?.id}`;

/** True for a channel item whose link Core withheld. */
export const isWithheldItem = (item) => Boolean(item?.isProtected && !item?.url);

/**
 * One answer per block+key for the page's lifetime. A failed reveal (rate limit, network) is not
 * remembered, so the next tap tries again.
 */
const revealCache = new Map();

const cacheKey = (blockId, key) => `${blockId}|${key}`;

function fetchReveal(blockId, keys) {
  return fetch(`${BASE_URL}/api/contact-blocks/${blockId}/reveal`, {
    method: 'POST',
    headers: {
      'Content-type': 'application/json',
    },
    body: JSON.stringify({ keys }),
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`Contact reveal failed with ${response.status}`);
    }

    return response.json();
  });
}

/**
 * Resolves several values of one block at once: `entries` are `{ key, value, isMasked }`, where
 * `isMasked` defaults to "the value carries a mask". Unmasked entries answer with their own value
 * and no request; masked ones not yet cached go out together, in as few requests as the key limit
 * allows. The result maps key → `{ value, href }` — both null for a field Core knows to be empty —
 * or to null when the reveal failed: any refusal (400 bad key, 403 locked or private page, 404 gone,
 * 409 not protected, 429) or a network error. The caller then simply does nothing. Never rejects.
 */
export function resolveMany({ blockId, entries }) {
  const result = {};
  const pending = [];
  const toFetch = [];

  (entries ?? []).forEach(({ key, value, isMasked }) => {
    const masked = isMasked ?? isMaskedValue(value);

    if (!masked) {
      result[key] = { value: value ?? null, href: null };
      return;
    }

    if (!blockId) {
      result[key] = null;
      return;
    }

    const id = cacheKey(blockId, key);

    if (revealCache.has(id)) {
      pending.push(revealCache.get(id).then((answer) => (result[key] = answer)));
    } else if (!toFetch.includes(key)) {
      toFetch.push(key);
    }
  });

  for (let index = 0; index < toFetch.length; index += REVEAL_BATCH) {
    const keys = toFetch.slice(index, index + REVEAL_BATCH);
    const request = fetchReveal(blockId, keys).then(
      (body) => body?.values ?? {},
      () => null,
    );

    keys.forEach((key) => {
      const id = cacheKey(blockId, key);
      const answer = request.then((values) => {
        const entry = values?.[key];

        // A failed request, or a key Core left out: not remembered, so the next tap asks again.
        if (!entry || typeof entry !== 'object') {
          revealCache.delete(id);
          return null;
        }

        return { value: entry.value ?? null, href: entry.href ?? null };
      });

      revealCache.set(id, answer);
      pending.push(answer.then((value) => (result[key] = value)));
    });
  }

  return Promise.all(pending).then(() => result);
}

/** One value: `{ value, href }`, answered without a request when the value is not masked. */
export function resolveValue({ blockId, key, value, isMasked }) {
  return resolveMany({ blockId, entries: [{ key, value, isMasked }] }).then((result) => result[key] ?? null);
}

/** Drops every revealed value. For tests; a page never needs it. */
export function clearRevealCache() {
  revealCache.clear();
}

const APP_SCHEME = /^(tel|mailto|sms|viber):/i;

/** Channel types whose link hands over to an app on the device rather than opening a page. */
const APP_TYPES = ['phone', 'email', 'sms', 'viber'];

const isPromise = (value) => Boolean(value) && typeof value.then === 'function';

/**
 * Opens a contact link. `href` is the link or a promise of it (a reveal in flight).
 *
 * tel:/mailto:/sms:/viber: go through location.href — they hand over to an app and leave the page
 * where it is. A web link opens in a new tab; when it is still being fetched, the tab is opened
 * blank right now, while the tap still counts as the visitor's gesture, and pointed at the link
 * once it arrives — opened after the await, it would be a popup and most browsers block it.
 * `type` is what tells the two apart before the link is known. If that tab was blocked, the page
 * itself goes to the link once it arrives. When no link arrives (the reveal was refused, or the
 * field is empty) the blank tab is closed and nothing else happens. Never rejects.
 */
export function openContactHref(href, type) {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  const isPending = isPromise(href);
  const opensApp = isPending ? APP_TYPES.includes(type) : APP_SCHEME.test(href ?? '');

  let tab = null;

  if (isPending && !opensApp) {
    tab = window.open('', '_blank');

    if (tab) {
      tab.opener = null;
    }
  }

  return Promise.resolve(href)
    .then(
      (url) => {
        if (!url || isDangerousUrl(url)) {
          tab?.close();
          return;
        }

        if (APP_SCHEME.test(url)) {
          tab?.close();
          window.location.href = url;
          return;
        }

        if (tab) {
          tab.location.href = url;
          return;
        }

        // No tab to point (it was blocked — in-app browsers such as Instagram's and TikTok's do — or
        // the link turned out to be a web page after all): a window.open now, after the await, would
        // be blocked too, so this page goes there instead.
        if (isPending) {
          window.location.href = url;
          return;
        }

        window.open(url, '_blank', 'noopener');
      },
      () => tab?.close(),
    )
    .catch(() => tab?.close());
}

function copyWithCommand(text) {
  if (typeof document === 'undefined') {
    return false;
  }

  const field = document.createElement('textarea');
  field.value = text;
  field.setAttribute('readonly', '');
  field.style.position = 'fixed';
  field.style.opacity = '0';
  document.body.appendChild(field);
  field.select();

  let isCopied = false;

  try {
    isCopied = document.execCommand('copy');
  } catch {
    isCopied = false;
  }

  document.body.removeChild(field);

  return isCopied;
}

function copyResolved(text) {
  return Promise.resolve(text)
    .then((value) => {
      if (!value) {
        return false;
      }

      const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : null;

      if (clipboard?.writeText) {
        return clipboard.writeText(String(value)).then(
          () => true,
          () => copyWithCommand(String(value)),
        );
      }

      return copyWithCommand(String(value));
    })
    .catch(() => false);
}

/**
 * Copies `text`, which may be a promise (a reveal in flight). Resolves true once copied.
 *
 * A ClipboardItem built from a promise is written within the tap itself, which is what Safari
 * needs: it refuses writeText once an await has passed. Browsers without it get writeText, then
 * the old execCommand.
 */
export function copyText(text) {
  const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : null;

  if (clipboard?.write && typeof ClipboardItem !== 'undefined') {
    try {
      const blob = Promise.resolve(text).then((value) => {
        if (!value) {
          throw new Error('Nothing to copy');
        }

        return new Blob([String(value)], { type: 'text/plain' });
      });

      // The clipboard owns this promise; if it never reads it (a ClipboardItem that throws on a
      // promise), a refused reveal must not surface as an unhandled rejection.
      blob.catch(() => {});

      return clipboard.write([new ClipboardItem({ 'text/plain': blob })]).then(
        () => true,
        () => copyResolved(text),
      );
    } catch {
      // A browser whose ClipboardItem takes no promise throws here; the fallbacks below cover it.
    }
  }

  return copyResolved(text);
}
